import { FC, useState } from 'react'
import moment from 'moment'
import { ReactComponent as ComparisonReportIcon } from 'src/assets/svg/comparison-report-64.svg'
import { ComparisonHistoryPanel } from 'src/components/ComparisonHistoryPanel'
import { Report } from 'src/charts/YourReports/interfaces/report.interface'
import { DataComparisonSummary } from 'src/services/dataComparisonApi/interfaces/dataComparison.interface'
import { NewComparisonModal, NewComparisonPayload } from '../NewComparisonModal'

interface ComparisonHistoryProps {
  comparisons: DataComparisonSummary[]
  isLoading?: boolean
  // Confirming the modal saves the comparison.
  onAddNew: (payload: NewComparisonPayload) => void
  onDelete: (comparisonId: string) => void
  // Saves the comparison's PDF — rendered on demand by the backend. Awaited so
  // the row shows its downloading state for as long as that actually takes.
  onDownload: (comparisonId: string) => Promise<void>
  // Opens the comparison's report, computed fresh from the saved windows.
  onOpen: (comparisonId: string) => void
}

const COPY = {
  addNew: 'Add New Comparison',
  // The row's own title is a pair of date ranges — too long to read back in a
  // confirmation, and the modal sits directly over the row being deleted.
  deleteTarget: 'this comparison',
  // Two periods of one campaign, which is what this list compares.
  pill: 'Campaign Comparison',
  subtitle: 'Compare campaign performance across different timeframes.',
  title: 'Reporting & Comparison',
}

// The card's copy differs from Your Reports only in the noun.
const CARD_COPY = {
  failedSubtext:
    'We couldn’t generate this comparison due to a temporary issue.',
  failedTitle: (report: Report) => report.name,
  generating: 'Generating comparison…',
  retry: 'Retry Generate Comparison',
}

const RANGE_FORMAT = 'MMM D, YYYY'

// Read back in UTC, which is how the window was stored — rendering a
// UTC-midnight boundary in local time would show the day before.
const formatWindow = ({ from, to }: { from: string; to: string }) =>
  `${moment.utc(from).format(RANGE_FORMAT)} – ${moment
    .utc(to)
    .format(RANGE_FORMAT)}`

// The row reuses Your Reports' card, so a comparison is presented in that
// card's shape: the pair of periods is the name, and the date is when the
// definition was saved. No file exists until a download renders one, so no
// size is claimed.
const toReport = (comparison: DataComparisonSummary): Report => ({
  dateRange: '',
  generatedAt: moment(comparison.createdAt).format(RANGE_FORMAT),
  id: comparison.id,
  modules: [],
  name: `${formatWindow(comparison.primaryRange)} — ${formatWindow(
    comparison.comparisonRange
  )}`,
  // A saved definition has no run behind it: the report is computed when the
  // row is opened, so every row is openable. The panel owns the client-only
  // `downloading` state while a PDF is being fetched.
  status: 'ready',
})

export const ComparisonHistory: FC<ComparisonHistoryProps> = ({
  comparisons,
  isLoading = false,
  onAddNew,
  onDelete,
  onDownload,
  onOpen,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <>
      <ComparisonHistoryPanel
        addLabel={COPY.addNew}
        deleteTarget={COPY.deleteTarget}
        icon={ComparisonReportIcon}
        isLoading={isLoading}
        onAdd={() => setIsModalOpen(true)}
        onDelete={onDelete}
        onDownload={onDownload}
        onOpen={onOpen}
        // A saved definition has no run left to fail, so the panel's retry
        // branch never renders.
        onRetry={() => undefined}
        pillLabel={COPY.pill}
        rowCopy={CARD_COPY}
        rows={comparisons.map(toReport)}
        subtitle={COPY.subtitle}
        title={COPY.title}
      />

      <NewComparisonModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={(payload) => {
          setIsModalOpen(false)
          onAddNew(payload)
        }}
      />
    </>
  )
}
